import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/ToastContainer';
import GuestLinkManager from '../components/GuestLinkManager';
import UserManagement from '../components/UserManagement';
import { useAuth } from '../contexts/AuthContext';

interface Quiz {
  id: number;
  title: string;
  description: string;
  created_at: string;
  category?: string;
  quiz_type?: 'quiz' | 'evaluation' | 'assessment' | 'survey';
}

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { isAdmin, isSupervisor, isLimitedAdmin } = useAuth();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingQuizId, setDeletingQuizId] = useState<number | null>(null);
  
  // Initialize state from URL params
  const [activeTab, setActiveTab] = useState<'quizzes' | 'guest-links' | 'users'>(
    (searchParams.get('tab') as 'quizzes' | 'guest-links' | 'users') || 'quizzes'
  );
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState<string>(searchParams.get('category') || 'all');
  const [quizTypeFilter, setQuizTypeFilter] = useState<string>(searchParams.get('quizType') || 'all');
  
  // Track if this is the initial mount to avoid creating history entry on first render
  const isInitialMount = useRef(true);
  // Track previous searchParams to detect browser navigation
  const prevSearchParamsRef = useRef<string>(searchParams.toString());

  useEffect(() => {
    fetchQuizzes();
  }, []);

  // Sync state FROM URL params when they change (browser back/forward)
  useEffect(() => {
    const currentParamsString = searchParams.toString();
    const prevParamsString = prevSearchParamsRef.current;
    
    // Only sync if URL params actually changed (browser navigation)
    if (currentParamsString !== prevParamsString) {
      prevSearchParamsRef.current = currentParamsString;
      
      const urlTab = (searchParams.get('tab') as 'quizzes' | 'guest-links' | 'users') || 'quizzes';
      const urlSearch = searchParams.get('search') || '';
      const urlCategory = searchParams.get('category') || 'all';
      const urlQuizType = searchParams.get('quizType') || 'all';
      
      // Only update state if it differs from URL (browser back/forward navigation)
      // This prevents unnecessary updates when we just set the URL ourselves
      if (urlTab !== activeTab) {
        setActiveTab(urlTab);
      }
      if (urlSearch !== search || urlCategory !== categoryFilter || urlQuizType !== quizTypeFilter) {
        setSearch(urlSearch);
        setCategoryFilter(urlCategory);
        setQuizTypeFilter(urlQuizType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sync URL params when filters change (user actions)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevSearchParamsRef.current = searchParams.toString();
      return;
    }

    const params = new URLSearchParams();
    if (activeTab !== 'quizzes') params.set('tab', activeTab);
    if (search) params.set('search', search);
    if (categoryFilter !== 'all') params.set('category', categoryFilter);
    if (quizTypeFilter !== 'all') params.set('quizType', quizTypeFilter);
    
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    
    // Only update if params actually changed
    if (currentParamsString !== newParamsString) {
      prevSearchParamsRef.current = newParamsString;
      setSearchParams(params, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, search, categoryFilter, quizTypeFilter]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/quizzes');
      setQuizzes(response.data);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching quizzes:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch quizzes';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (id: number, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setDeletingQuizId(id);
      await api.delete(`/api/quizzes/${id}`);
      
      // Remove the deleted quiz from the state
      setQuizzes(prev => prev.filter(quiz => quiz.id !== id));
      showToast(`Quiz "${title}" deleted successfully`, 'success');
    } catch (err: any) {
      console.error('Error deleting quiz:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete quiz';
      showToast(errorMessage, 'error');
    } finally {
      setDeletingQuizId(null);
    }
  };

  const handleCopyShareLink = (quizId: number, title: string) => {
    const shareLink = `${window.location.origin}/quiz/${quizId}`;
    navigator.clipboard.writeText(shareLink).then(() => {
      showToast(`Share link for "${title}" copied to clipboard!`, 'success');
    }).catch(() => {
      showToast('Failed to copy link to clipboard', 'error');
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = () => {
    fetchQuizzes();
    showToast('Refreshing quiz list...', 'info');
  };

  // Get unique categories from quizzes
  const categories = Array.from(new Set(quizzes.map(quiz => quiz.category).filter(Boolean)));

  // Get unique quiz types from quizzes
  const quizTypes = Array.from(new Set(quizzes.map(quiz => quiz.quiz_type).filter(Boolean)));

  // Get quiz count for each category
  const getCategoryQuizCount = (category: string) => {
    return quizzes.filter(quiz => quiz.category === category).length;
  };

  // Get quiz count for each quiz type
  const getQuizTypeCount = (quizType: string) => {
    return quizzes.filter(quiz => quiz.quiz_type === quizType).length;
  };

  const filteredQuizzes = useMemo(() => {
    let filtered = quizzes;
    
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(quiz => quiz.category === categoryFilter);
    }
    
    // Apply quiz type filter
    if (quizTypeFilter !== 'all') {
      filtered = filtered.filter(quiz => quiz.quiz_type === quizTypeFilter);
    }
    
    // Apply search filter
    const q = search.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((quiz) => {
        const title = (quiz.title || '').toLowerCase();
        const desc = (quiz.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }
    
    return filtered;
  }, [quizzes, search, categoryFilter, quizTypeFilter]);

  const handleCategoryFilterChange = (newFilter: string) => {
    setCategoryFilter(newFilter);
    // URL will be updated by the useEffect hook above
  };

  const handleQuizTypeFilterChange = (newFilter: string) => {
    setQuizTypeFilter(newFilter);
    // URL will be updated by the useEffect hook above
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1 className="page-title">
              {isAdmin ? 'Admin Dashboard' : isLimitedAdmin ? 'CES Admin Dashboard' : 'Supervisor Dashboard'}
            </h1>
            {isAdmin && (
              <Link to="/admin/quiz/create" className="btn btn-primary">
                Create New Quiz
              </Link>
            )}
          </div>
          <div className="card text-center">
            <div className="loading-spinner"></div>
            <p>Loading quizzes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-dashboard">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="page-title">
            {isAdmin ? 'Admin Dashboard' : isLimitedAdmin ? 'CES Admin Dashboard' : 'Supervisor Dashboard'}
          </h1>
          <div className="d-flex gap-2">
            <button onClick={handleRefresh} className="btn btn-outline" title="Refresh">
              Refresh
            </button>
            {isAdmin && (
              <Link to="/admin/quiz/create" className="btn btn-primary">
                Create New Quiz
              </Link>
            )}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="tabs">
          <button 
            className={`tab-button ${activeTab === 'quizzes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quizzes')}
          >
            Quiz Management
          </button>
          {isAdmin && (
            <>
              <button 
                className={`tab-button ${activeTab === 'guest-links' ? 'active' : ''}`}
                onClick={() => setActiveTab('guest-links')}
              >
                Guest Links
              </button>
              <button 
                className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                User Management
              </button>
            </>
          )}
        </div>
        
        {/* Tab Content */}
        {activeTab === 'quizzes' && (
          <div className="tab-content" key="quizzes">
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
            <button 
              onClick={handleRefresh}
              className="btn btn-small btn-outline mt-2"
            >
              Try Again
            </button>
          </div>
        )}
        
        {quizzes.length === 0 ? (
          <div className="card text-center">
            <h2>Welcome to Quiz Admin!</h2>
            <p>Create your first quiz to get started.</p>
            <Link to="/admin/quiz/create" className="btn btn-primary btn-large mt-2">
              Create Your First Quiz
            </Link>
          </div>
        ) : (
          <>
            <div className="kpi-header-row mb-2">
              <h2 className="kpi-title">Dashboard Overview</h2>
              <div className="dashboard-search">
                <div className="search-input-wrapper">
                  <span className="search-icon" aria-hidden>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="m17.5 17.5-2.917-2.917m2.084-5a7.083 7.083 0 1 1-14.167 0 7.083 7.083 0 0 1 14.167 0z" stroke="#667085" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <input
                    className="dashboard-search-input"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="category-filter-bubbles mb-3">
              <div 
                className={`category-bubble ${categoryFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleCategoryFilterChange('all')}
              >
                <span className="category-name">All Quizzes</span>
                <span className="category-count">{quizzes.length}</span>
              </div>
              {categories
                .map(category => ({
                  category,
                  count: getCategoryQuizCount(category!)
                }))
                .sort((a, b) => b.count - a.count)
                .map(({ category, count }) => (
                  <div 
                    key={category}
                    className={`category-bubble ${categoryFilter === category ? 'active' : ''}`}
                    onClick={() => handleCategoryFilterChange(category!)}
                  >
                    <span className="category-name">{category}</span>
                    <span className="category-count">{count}</span>
                  </div>
                ))}
            </div>

            {filteredQuizzes.length === 0 ? (
              <div className="empty-search-note">No quizzes match your search.</div>
            ) : (
              <div className="quiz-list">
                {filteredQuizzes.map(quiz => (
                  <div key={quiz.id} className="card quiz-card shadow-hover">
                  <div className="quiz-card-inner">
                    <div className="quiz-card-header">
                      <h2 className="quiz-card-title">{quiz.title}</h2>
                      <div className="quiz-status">
                        <span className="status-badge status-active">Active</span>
                        {quiz.quiz_type && quiz.quiz_type !== 'quiz' && (
                          <span className={`status-badge ${quiz.quiz_type === 'evaluation' ? 'status-evaluation' : 'status-assessment'}`}>
                            {quiz.quiz_type === 'evaluation' ? 'Evaluation' :
                             quiz.quiz_type === 'assessment' ? 'Assessment' :
                             quiz.quiz_type === 'survey' ? 'Survey' : quiz.quiz_type}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="quiz-card-date-pill">
                      <span className="date-icon" aria-hidden></span>
                      <span className="date-text">{formatDate(quiz.created_at)}</span>
                    </div>
                    <p className="quiz-card-description">{quiz.description || 'No description provided'}</p>
                  </div>
                  
                  <div className="quiz-card-share">
                    <label className="share-label">Share this quiz</label>
                    <div className="share-link">
                      <span className="share-addon"><span className="share-addon-text">http://</span></span>
                      <input
                        type="text"
                        value={`${window.location.host}/quiz/${quiz.id}`}
                        readOnly
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                        className="form-control"
                        aria-label="Share URL"
                      />
                      <button
                        className="btn btn-copy btn-small"
                        onClick={() => handleCopyShareLink(quiz.id, quiz.title)}
                        title="Copy link"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  
                  <div className="quiz-card-actions">
                    <Link 
                      to={`/quiz/${quiz.id}`} 
                      className="btn btn-preview btn-small"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Preview quiz"
                    >
                      Preview
                    </Link>
                    
                    {(isAdmin || isLimitedAdmin) && (
                      <Link 
                        to={`/admin/quiz/${quiz.id}/edit`} 
                        className="btn btn-edit btn-small"
                        title="Edit quiz"
                      >
                        Edit
                      </Link>
                    )}
                    
                    <Link 
                      to={`/admin/quiz/${quiz.id}/responses`} 
                      className="btn btn-responses btn-small"
                      title="View responses"
                    >
                      Responses
                    </Link>
                    
                    {isAdmin && (
                      <button 
                        className="btn btn-delete btn-small"
                        onClick={() => handleDeleteQuiz(quiz.id, quiz.title)}
                        disabled={deletingQuizId === quiz.id}
                        title="Delete quiz"
                      >
                        {deletingQuizId === quiz.id ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
          </div>
        )}

        {activeTab === 'guest-links' && isAdmin && (
          <div className="tab-content" key="guest-links">
            <GuestLinkManager />
          </div>
        )}

        {activeTab === 'users' && isAdmin && (
          <div className="tab-content" key="users">
            <UserManagement />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage; 